// frontend/src/components/ReportesTab.tsx
// Centro de reportes — Nómina de Presentismo oficial Stitch Institutional

import { useState, useEffect, useRef } from 'react';
import { api, MSG_REQUIERE_BACKEND_NUEVO, esErrorDeRed, requiereBackendNuevo } from '../lib/api';
import type { ParametrosCompania, ParteNovedades, ReporteNovedades, TipoLicencia } from '../lib/api';
import { storage } from '../services/storage';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { COMPANIAS } from './TopAppBar';
import {
  AlertCircle, Award, Pencil, PlusCircle, RefreshCw, Save, Trash2,
  FileSpreadsheet, Users, FileText, CalendarRange, ClipboardList, RotateCcw,
  ScrollText, Eye, Check,
} from 'lucide-react';
import { leerCompaniaSesionGuardada } from '../lib/companiaSesion';
import { esEfectivoEnFecha, getArgentinaDateString } from '../lib/utils';
import {
  CODIGOS_NOVEDAD, NOMBRE_CODIGO, codigoDeMotivo, estiloCodigo, fechaCorta, rangoMes,
  tiposLicenciaActivosOrdenados, validarRango,
} from '../lib/novedades';
import { cargarSelloBase64, descargarBlob, escapeHtml, footerSelloFirmaHtml, htmlAPdf } from '../lib/reportePdf';
import { construirPaginasNovedades } from '../lib/reporteNovedadesPdf';
import { toast } from '../lib/toast';
// exceljs se carga bajo demanda (import dinámico) en el handler de Excel; jspdf /
// html2canvas los carga reportePdf.ts igual: así no inflan el chunk principal
// (límite de precache de Workbox) ni se descargan si nunca se genera un reporte.

// Licencias vigentes en una fecha: fecha_fin null = licencia abierta (hasta alta)
function licenciaVigente(lic: any, fecha: string): boolean {
  return !!lic && !!lic.fecha_inicio && lic.fecha_inicio <= fecha && (!lic.fecha_fin || lic.fecha_fin >= fecha);
}

// Código de la columna para un ausente según el motivo registrado en CadeteCard
// (lógica compartida en lib/novedades.ts: codigoDeMotivo).
const codigoAusencia = codigoDeMotivo;

// Id de compañía (1–13) a partir del nombre del selector; 0 si no es válido
function companiaIdDe(nombre: string): number {
  const i = COMPANIAS.indexOf(nombre);
  return i >= 0 ? i + 1 : 0;
}

// Código de tipo de licencia: 2 a 4 letras mayúsculas (RMH, ART, LE, PE…)
const CODIGO_TIPO_REGEX = /^[A-Z]{2,4}$/;

// Mensaje para errores de los endpoints del Parte de Novedades (backend nuevo)
const MSG_PARTE_SIN_CONEXION = 'Sin conexión: este reporte se genera en el servidor.';
function mensajeErrorParte(err: unknown, porDefecto: string): string {
  if (requiereBackendNuevo(err)) return MSG_REQUIERE_BACKEND_NUEVO;
  if (esErrorDeRed(err) || !navigator.onLine) return MSG_PARTE_SIN_CONEXION;
  const msg = (err as { message?: string } | null)?.message;
  return msg && msg !== 'HTTP_ERROR' && msg !== 'SERVER_UNAVAILABLE' ? msg : porDefecto;
}

// Placeholders con el formato oficial del encabezado del parte
const PLACEHOLDER_JEFE_SECCION = 'JEFE DE SECCION CUERPO DE CADETES: SUBCOMISARIO (Cdo.) APELLIDO NOMBRE';
const PLACEHOLDER_INSTRUCTORES = 'INSTRUCTOR: OFL. AYUDANTE (Cdo.) APELLIDO NOMBRE - OFL. AYUDANTE (Cdo.) APELLIDO NOMBRE';

// Texto de la columna "Horario de Presentismo" para un cadete
function horarioPresentismoDe(matchingReg: any, codigoLicencia: string | undefined): string {
  if (matchingReg) {
    if (matchingReg.estado === 'presente' || matchingReg.estado === 'tarde') {
      return matchingReg.hora_local ? String(matchingReg.hora_local).substring(0, 5) : '-';
    }
    if (matchingReg.estado === 'ausente') {
      return codigoAusencia(matchingReg.motivo) ?? (codigoLicencia ? codigoLicencia.toUpperCase() : 'ACA');
    }
    return '-';
  }
  // Sin registro del día: si tiene licencia vigente se informa su código
  return codigoLicencia ? codigoLicencia.toUpperCase() : '-';
}

function Toast({ visible, error }: { visible: boolean; error: string | null }) {
  if (!visible && !error) return null;
  return (
    <div
      className="fixed z-[9000] flex items-center gap-2 px-5 py-3 rounded-full shadow-lg text-sm font-medium"
      style={{
        top: 'calc(env(safe-area-inset-top) + 64px)',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#303030',
        color: '#f3f0f0',
        whiteSpace: 'nowrap',
        animation: 'slideUp 0.25s ease-out',
      }}
    >
      {error ? (
        <>
          <AlertCircle size={18} strokeWidth={2} style={{ color: '#f87171', flexShrink: 0 }} />
          {error}
        </>
      ) : (
        <>
          <RefreshCw size={18} strokeWidth={2} className="animate-spin-slow shrink-0" />
          Generando reporte...
        </>
      )}
    </div>
  );
}

interface ReportesTabProps {
  fecha: string;
  // Compañía fija de la sesión para usuarios no-admin (null/undefined = libre)
  companiaForzada?: string | null;
}

export function ReportesTab({ fecha, companiaForzada }: ReportesTabProps) {
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // States for Admin catalogue management
  const [isAdmin, setIsAdmin] = useState(false);
  const [tiposLicencias, setTiposLicencias] = useState<TipoLicencia[]>([]);
  const [loadingLicencias, setLoadingLicencias] = useState(false);
  const [modalLicenciaOpen, setModalLicenciaOpen] = useState(false);
  const [editingLicencia, setEditingLicencia] = useState<TipoLicencia | null>(null);
  
  // Form states
  const [formNombre, setFormNombre] = useState('');
  const [formCodigo, setFormCodigo] = useState('');
  const [formDescripcion, setFormDescripcion] = useState('');
  const [formCargando, setFormCargando] = useState(false);

  // States for Nómina de Presentismo
  const [selectedCompania, setSelectedCompania] = useState<string>(() => {
    // Usuarios no-admin: por defecto la compañía de su sesión
    if (companiaForzada) return companiaForzada;
    const guardada = leerCompaniaSesionGuardada();
    if (guardada && !guardada.esAdmin && guardada.compania) return guardada.compania;
    const saved = localStorage.getItem('sgcc_compania_v4');
    return saved && COMPANIAS.includes(saved) ? saved : 'Décima Compañía';
  });
  // No-admin: la compañía del reporte está fijada a la de su sesión
  const companiaReporte = companiaForzada || selectedCompania;
  const [selectedFecha, setSelectedFecha] = useState<string>(fecha);
  const fechaValida = /^\d{4}-\d{2}-\d{2}$/.test(selectedFecha);

  // Novedades y Licencias por período (backend nuevo): compañía propia del
  // reporte (misma regla: fija para no-admin), rango por defecto = mes en curso.
  const [novCompania, setNovCompania] = useState<string>(selectedCompania);
  const novCompaniaReporte = companiaForzada || novCompania;
  const [novDesde, setNovDesde] = useState<string>(() => rangoMes(getArgentinaDateString()).desde);
  const [novHasta, setNovHasta] = useState<string>(() => rangoMes(getArgentinaDateString()).hasta);
  const novErrorRango = validarRango(novDesde, novHasta);
  const [novReporte, setNovReporte] = useState<ReporteNovedades | null>(null);
  const [novCargando, setNovCargando] = useState<'resumen' | 'excel' | 'pdf' | null>(null);
  const [novError, setNovError] = useState<string | null>(null);
  // El resumen en pantalla corresponde a la selección actual
  const novReporteVigente = novReporte
    && novReporte.compania?.id === companiaIdDe(novCompaniaReporte)
    && novReporte.desde === novDesde && novReporte.hasta === novHasta
    ? novReporte : null;

  // Parte de Novedades (backend nuevo): parte diario oficial de una compañía en
  // una fecha. Compañía fija para no-admin; fecha por defecto hoy (Argentina),
  // nunca futura. El encabezado (jefe de sección / instructores) se guarda por
  // compañía en el servidor al perder el foco de los campos si cambió.
  const hoyArgentina = getArgentinaDateString();
  const [parteCompania, setParteCompania] = useState<string>(selectedCompania);
  const parteCompaniaReporte = companiaForzada || parteCompania;
  const parteCompaniaId = companiaIdDe(parteCompaniaReporte);
  const [parteFecha, setParteFecha] = useState<string>(hoyArgentina);
  const parteErrorFecha = !/^\d{4}-\d{2}-\d{2}$/.test(parteFecha)
    ? 'Seleccione una fecha para el parte.'
    : parteFecha > hoyArgentina ? 'La fecha del parte no puede ser futura.' : null;
  const [parteJefe, setParteJefe] = useState('');
  const [parteInstructores, setParteInstructores] = useState('');
  // Últimos valores confirmados por el servidor (para detectar cambios al perder el foco)
  const [parteParamsServidor, setParteParamsServidor] = useState<ParametrosCompania | null>(null);
  const [parteParamsEstado, setParteParamsEstado] = useState<'cargando' | 'guardando' | 'guardado' | null>(null);
  const [parteParamsError, setParteParamsError] = useState<string | null>(null);
  const parteParamsRequest = useRef(0);
  const [parte, setParte] = useState<ParteNovedades | null>(null);
  const [parteCargando, setParteCargando] = useState<'vista' | 'excel' | null>(null);
  const [parteError, setParteError] = useState<string | null>(null);
  // La vista previa en pantalla corresponde a la selección actual
  const parteVigente = parte && parte.compania?.id === parteCompaniaId && parte.fecha === parteFecha ? parte : null;
  const parteSinMarcar = parteVigente
    ? Math.max(0, parteVigente.totales.efectiva - (parteVigente.totales.presentes + parteVigente.totales.ausentes))
    : 0;

  useEffect(() => {
    if (fecha) setSelectedFecha(fecha);
  }, [fecha]);

  // Encabezado del parte: se recarga al cambiar la compañía (se descartan
  // respuestas de una compañía anterior con el contador de request).
  useEffect(() => {
    const req = ++parteParamsRequest.current;
    setParteParamsServidor(null);
    setParteParamsError(null);
    setParteParamsEstado(null);
    if (!parteCompaniaId) return;
    if (!navigator.onLine) {
      setParteParamsError('Sin conexión: el encabezado del parte se guarda en el servidor.');
      return;
    }
    setParteParamsEstado('cargando');
    api.getParametrosCompania(parteCompaniaId)
      .then((res) => {
        if (req !== parteParamsRequest.current) return;
        if (!res.success || !res.data) throw new Error(res.error || 'No se pudo cargar el encabezado del parte.');
        const datos = { jefe_seccion: res.data.jefe_seccion || '', instructores: res.data.instructores || '' };
        setParteParamsServidor(datos);
        setParteJefe(datos.jefe_seccion);
        setParteInstructores(datos.instructores);
        setParteParamsEstado(null);
      })
      .catch((err: unknown) => {
        if (req !== parteParamsRequest.current) return;
        setParteParamsEstado(null);
        setParteParamsError(mensajeErrorParte(err, 'No se pudo cargar el encabezado del parte.'));
      });
  }, [parteCompaniaId]);

  useEffect(() => {
    // Verificar rol de administrador
    const authUserStr = localStorage.getItem('auth_user');
    if (authUserStr) {
      try {
        const user = JSON.parse(authUserStr);
        if (user.scope === 'admin') {
          setIsAdmin(true);
        }
      } catch (e) {
        console.error('Error al parsear auth_user:', e);
      }
    }
    cargarTiposLicencias();
  }, []);

  const cargarTiposLicencias = async () => {
    try {
      setLoadingLicencias(true);
      const locales = await storage.getTiposLicencias();
      setTiposLicencias(locales);
      
      if (navigator.onLine) {
        // El backend filtra por ?activo (sin parámetro devuelve solo activos):
        // para el panel admin se piden también los inactivos y se guardan todos
        // en el catálogo local (la UI filtra activos donde se elige un tipo).
        const esAdmin = leerCompaniaSesionGuardada()?.esAdmin === true;
        const [activos, inactivos] = await Promise.all([
          api.getTiposLicencias(true),
          esAdmin ? api.getTiposLicencias(false).catch(() => null) : Promise.resolve(null),
        ]);
        if (activos.success && activos.data) {
          const idsActivos = new Set(activos.data.map((t) => t.id));
          const extra = (inactivos?.success && inactivos.data ? inactivos.data : [])
            .filter((t) => !idsActivos.has(t.id))
            .map((t) => ({ ...t, activo: false }));
          const todos = [...activos.data, ...extra];
          setTiposLicencias(todos);
          await storage.setTiposLicencias(todos);
        }
      }
    } catch (e) {
      console.error('Error al cargar tipos de licencias:', e);
    } finally {
      setLoadingLicencias(false);
    }
  };

  const openCreateModal = () => {
    setEditingLicencia(null);
    setFormNombre('');
    setFormCodigo('');
    setFormDescripcion('');
    setModalLicenciaOpen(true);
  };
  
  const openEditModal = (tipo: TipoLicencia) => {
    setEditingLicencia(tipo);
    setFormNombre(tipo.nombre);
    setFormCodigo(tipo.codigo);
    setFormDescripcion(tipo.descripcion || '');
    setModalLicenciaOpen(true);
  };
  
  const closeModal = () => {
    setModalLicenciaOpen(false);
    setEditingLicencia(null);
    setFormNombre('');
    setFormCodigo('');
    setFormDescripcion('');
  };

  const handleSaveTipoLicencia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formNombre.trim() || !formCodigo.trim()) {
      toast.error('Por favor complete los campos obligatorios.');
      return;
    }
    const codigoNormalizado = formCodigo.trim().toUpperCase();
    if (!CODIGO_TIPO_REGEX.test(codigoNormalizado)) {
      toast.error('El código debe tener entre 2 y 4 letras mayúsculas (ej: RMH, ART, LE, PE).');
      return;
    }
    
    try {
      setFormCargando(true);
      const payload = {
        nombre: formNombre.trim(),
        codigo: codigoNormalizado,
        descripcion: formDescripcion.trim() || undefined,
        // El PUT del backend pone activo=true si no se envía: conservar el estado
        activo: editingLicencia ? editingLicencia.activo !== false : true,
      };
      
      if (editingLicencia) {
        if (navigator.onLine) {
          const res = await api.updateTipoLicencia(editingLicencia.id, payload);
          if (res.success) {
            toast.success('Tipo de licencia actualizado correctamente.');
          } else {
            throw new Error(res.error || 'Error al actualizar tipo de licencia');
          }
        } else {
          const locales = await storage.getTiposLicencias();
          const actualizados = locales.map((t: any) => 
            t.id === editingLicencia.id ? { ...t, ...payload } : t
          );
          await storage.setTiposLicencias(actualizados);
          toast.info('Sin conexión. Cambios guardados localmente.');
        }
      } else {
        if (navigator.onLine) {
          const res = await api.createTipoLicencia(payload);
          if (res.success) {
            toast.success('Tipo de licencia creado correctamente.');
          } else {
            throw new Error(res.error || 'Error al crear tipo de licencia');
          }
        } else {
          const tempId = Math.floor(Math.random() * 1000000);
          const localTipo = {
            id: tempId,
            ...payload,
            activo: true,
          };
          const locales = await storage.getTiposLicencias();
          await storage.setTiposLicencias([...locales, localTipo]);
          toast.info('Sin conexión. Tipo de licencia guardado localmente.');
        }
      }
      
      closeModal();
      await cargarTiposLicencias();
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error al guardar tipo de licencia.');
    } finally {
      setFormCargando(false);
    }
  };

  // Reactivar un tipo desactivado (PUT con activo: true; el backend exige nombre y código)
  const handleReactivarTipoLicencia = async (tipo: TipoLicencia) => {
    if (!confirm(`¿Reactivar el tipo de licencia "${tipo.codigo} — ${tipo.nombre}"?`)) return;
    try {
      setLoadingLicencias(true);
      const payload = { nombre: tipo.nombre, codigo: tipo.codigo, descripcion: tipo.descripcion || undefined, activo: true };
      if (navigator.onLine) {
        const res = await api.updateTipoLicencia(tipo.id, payload);
        if (!res.success) throw new Error(res.error || 'Error al reactivar el tipo de licencia');
      } else {
        const locales = await storage.getTiposLicencias();
        await storage.setTiposLicencias(locales.map((t: any) => (t.id === tipo.id ? { ...t, activo: true } : t)));
        toast.info('Sin conexión. Reactivado localmente.');
      }
      await cargarTiposLicencias();
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error al reactivar tipo de licencia.');
    } finally {
      setLoadingLicencias(false);
    }
  };

  const handleDeleteTipoLicencia = async (id: number) => {
    if (!confirm('¿Desactivar este tipo de licencia? Dejará de ofrecerse al cargar licencias (podrá reactivarlo desde este panel).')) {
      return;
    }
    
    try {
      setLoadingLicencias(true);
      if (navigator.onLine) {
        const res = await api.deleteTipoLicencia(id);
        if (res.success) {
          toast.success('Tipo de licencia desactivado correctamente.');
        } else {
          throw new Error(res.error || 'Error al eliminar');
        }
      } else {
        const locales = await storage.getTiposLicencias();
        const actualizados = locales.map((t: any) => 
          t.id === id ? { ...t, activo: false } : t
        );
        await storage.setTiposLicencias(actualizados);
        toast.info('Sin conexión. Removido localmente.');
      }
      await cargarTiposLicencias();
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error al eliminar tipo de licencia.');
    } finally {
      setLoadingLicencias(false);
    }
  };

  // Carga cadetes y registros para el reporte combinando servidor + IndexedDB.
  // Las marcas locales pendientes de sincronizar (y las desmarcaciones) siempre
  // ganan sobre lo que devuelve el servidor, y nunca se sobrescriben.
  const cargarDatosReporte = async (fechaReporte: string) => {
    // Online: la nómina oficial sale de la lista del servidor (los cadetes dados de
    // baja allí no deben aparecer aunque sigan en IndexedDB). Offline: datos locales.
    let idsServidor: Set<number> | null = null;
    let cadetesServidor: any[] | null = null;
    let licenciasServidor: any[] | null = null;

    if (navigator.onLine) {
      try {
        const res = await api.getCadetes();
        if (res.success && res.data && res.data.length > 0) {
          cadetesServidor = res.data;
          idsServidor = new Set(res.data.map((c: any) => c.id));
          // Lista completa: elimina locales no pendientes ausentes en el servidor;
          // respeta las ediciones locales pendientes
          try {
            await storage.mergeServerCadetes(res.data, { completo: true });
          } catch (mergeErr) {
            console.warn('No se pudo actualizar la caché local de cadetes:', mergeErr);
          }
        }
      } catch (syncErr) {
        console.warn('No se pudieron traer cadetes del servidor para el reporte, se usarán datos locales:', syncErr);
      }

      try {
        const resReg = await api.getRegistros(fechaReporte, fechaReporte);
        if (resReg.success && resReg.data) {
          // Ventana completa: refleja borrados del servidor; nunca pisa pendientes locales
          await storage.saveRegistrosMerge(resReg.data, { desde: fechaReporte, hasta: fechaReporte });
        }
      } catch (err) {
        console.warn('Error al traer registros del servidor para el reporte:', err);
      }

      try {
        const resLic = await api.getLicencias(undefined, fechaReporte);
        if (resLic.success && resLic.data) licenciasServidor = resLic.data;
      } catch (err) {
        console.warn('Error al traer licencias del servidor para el reporte, se usarán las locales:', err);
      }
    }

    // IndexedDB ya contiene servidor + pendientes locales (los pendientes ganan);
    // los métodos de lectura excluyen las desmarcaciones (tombstones).
    const locales: any[] = await storage.getCadetes();
    let cadetes: any[] = locales;
    if (idsServidor) {
      const ids = idsServidor;
      // Versión local (con ediciones pendientes) de los cadetes que el servidor
      // reconoce, más altas locales aún no sincronizadas
      const enLocal = locales.filter((c) => ids.has(c.id) || c.pending_sync);
      const idsLocal = new Set(enLocal.map((c) => c.id));
      cadetes = [...enLocal, ...(cadetesServidor || []).filter((c) => !idsLocal.has(c.id))];
    }
    const registros: any[] = await storage.getRegistrosByFecha(fechaReporte);

    // Licencias vigentes en la fecha (incluye abiertas con fecha_fin null)
    const licenciasLocales: any[] = await storage.getLicencias().catch(() => []);
    const licencias = licenciasServidor
      ? [...licenciasServidor, ...licenciasLocales.filter((l) => l.pending_sync)]
      : licenciasLocales;
    const licenciaPorCadete = new Map<number, string>();
    licencias.forEach((lic) => {
      if (licenciaVigente(lic, fechaReporte) && !licenciaPorCadete.has(lic.cadete_id)) {
        licenciaPorCadete.set(lic.cadete_id, lic.tipo_codigo || 'LIC');
      }
    });

    return { cadetes, registros, licenciaPorCadete };
  };

  const handleExportExcel = async () => {
    setDescargando(true);
    setError(null);
    try {
      if (!fechaValida) {
        throw new Error('Seleccione una fecha válida para el reporte.');
      }
      const { cadetes, registros, licenciaPorCadete } = await cargarDatosReporte(selectedFecha);

      const filtrados = cadetes.filter((c) => {
        const matchesActivo = c.activo !== false;
        const matchesCompania = c.compania === companiaReporte;
        // Fuerza efectiva en la fecha del reporte (una baja posterior no lo saca)
        const esConformadorFuerzaEfectiva = esEfectivoEnFecha(c, selectedFecha);
        return matchesActivo && matchesCompania && esConformadorFuerzaEfectiva;
      });

      if (filtrados.length === 0) {
        throw new Error('No se encontraron cadetes activos para la compañía seleccionada.');
      }

      filtrados.sort((a, b) => {
        const apellidoA = (a.apellido || '').trim().toUpperCase();
        const apellidoB = (b.apellido || '').trim().toUpperCase();
        if (apellidoA !== apellidoB) {
          return apellidoA.localeCompare(apellidoB, 'es', { sensitivity: 'base' });
        }
        const nombresA = (a.nombres || '').trim().toUpperCase();
        const nombresB = (b.nombres || '').trim().toUpperCase();
        return nombresA.localeCompare(nombresB, 'es', { sensitivity: 'base' });
      });

      const [year, month, day] = selectedFecha.split('-');
      const fechaFormateada = `${day}/${month}/${year}`;

      // Crear el libro de trabajo (workbook) y hoja de cálculo (worksheet) con exceljs
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Presentismo');

      // Título en la Fila 1
      const titleRow = worksheet.addRow([
        `NÓMINA DE PRESENTISMO - COMPAÑÍA: ${companiaReporte.toUpperCase()}      Fecha: ${fechaFormateada}`
      ]);
      worksheet.mergeCells('A1:I1');
      titleRow.font = { name: 'Arial', size: 12, bold: true };
      titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
      titleRow.height = 30;

      // Fila 2: Vacía
      worksheet.addRow([]);

      // Fila 3: Encabezados
      const headerRow = worksheet.addRow([
        "N°",
        "Horario de Presentismo",
        "Apellido y Nombres",
        "Legajo",
        "DNI N°",
        "Localidad",
        "Teléfono Principal",
        "Teléfono Alternativo",
        "Firma del Cursante"
      ]);
      headerRow.font = { name: 'Arial', size: 10, bold: true };
      headerRow.eachCell((cell) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F0F0' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
      headerRow.height = 25;

      // Fila 4+: Datos de los cadetes
      filtrados.forEach((c, index) => {
        const nro = index + 1;
        const apellidoNombres = `${c.apellido || ''} ${c.nombres || ''}`.trim().toUpperCase();
        const legajoVal = (c.legajo !== null && c.legajo !== undefined && String(c.legajo).trim() !== '') ? String(c.legajo) : 'S/LEGAJO';
        const dniVal = c.dni || '';
        const localidadVal = (c.localidad || '').toUpperCase();
        const telPrincipalVal = c.telefono_principal || '';
        const telAlternativoVal = c.telefono_alternativo || '';

        const matchingReg = registros.find((r) => r.cadete_id === c.id);
        const horarioPresentismo = horarioPresentismoDe(matchingReg, licenciaPorCadete.get(c.id));

        const dataRow = worksheet.addRow([
          nro,
          horarioPresentismo,
          apellidoNombres,
          legajoVal,
          dniVal,
          localidadVal,
          telPrincipalVal,
          telAlternativoVal,
          "" // Firma vacía para completar a mano
        ]);

        dataRow.height = 20;
        dataRow.eachCell((cell, colNumber) => {
          cell.font = { name: 'Arial', size: 9 };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          // Alineación
          if (colNumber === 1 || colNumber === 2 || colNumber === 4 || colNumber === 5 || colNumber === 7 || colNumber === 8) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          }

          if (colNumber === 1) cell.font = { name: 'Arial', size: 9, bold: true };
          if (colNumber === 2 || colNumber === 3) cell.font = { name: 'Arial', size: 9, bold: true };
        });
      });

      // Filas vacías de separación antes del sello oficial
      worksheet.addRow([]);
      worksheet.addRow([]);

      const nextRowIdx = worksheet.rowCount + 1;

      // Intentar cargar la imagen del sello y meterla al Excel
      try {
        const response = await fetch('/sello-mds-pba.png');
        if (!response.ok) {
          throw new Error('No se pudo cargar la imagen del sello');
        }
        const imageBlob = await response.blob();
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(imageBlob);
        });

        const pureBase64 = base64Data.split(',')[1];
        const imageId = workbook.addImage({
          base64: pureBase64,
          extension: 'png',
        });

        // Insertar imagen en columna D (columna índice 3) a F (índice 6)
        worksheet.addImage(imageId, {
          tl: { col: 3, row: nextRowIdx - 1 } as any,
          br: { col: 6, row: nextRowIdx + 4 } as any,
          editAs: 'oneCell'
        });

        // Agregar filas vacías para que el sello no se superponga con la firma
        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);
      } catch (imgErr) {
        console.warn('No se pudo incrustar el sello en el Excel, cayendo a texto de respaldo:', imgErr);
        // Fallback en texto si falla la imagen
        const s1 = worksheet.addRow(["", "", "", "MINISTERIO DE DESARROLLO DE LA COMUNIDAD"]);
        const s2 = worksheet.addRow(["", "", "", "PROVINCIA DE BUENOS AIRES"]);
        const s3 = worksheet.addRow(["", "", "", "Sello de Constancia Oficial"]);
        s1.getCell(4).font = { name: 'Arial', size: 9, bold: true };
        s2.getCell(4).font = { name: 'Arial', size: 9, bold: true };
        s3.getCell(4).font = { name: 'Arial', size: 9, bold: true };
        worksheet.mergeCells(`D${nextRowIdx}:F${nextRowIdx}`);
        worksheet.mergeCells(`D${nextRowIdx+1}:F${nextRowIdx+1}`);
        worksheet.mergeCells(`D${nextRowIdx+2}:F${nextRowIdx+2}`);
      }

      // Filas vacías de separación antes de la firma
      worksheet.addRow([]);
      worksheet.addRow([]);

      const signatureRow = worksheet.addRow([
        "", "", "", "", "", "", "", "", "Firma y Aclaración de la Autoridad"
      ]);
      signatureRow.getCell(9).font = { name: 'Arial', size: 10, bold: true };
      signatureRow.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };
      signatureRow.getCell(9).border = {
        top: { style: 'hair' }
      };

      // Anchos de columna
      worksheet.columns = [
        { width: 6 },   // N°
        { width: 22 },  // Horario de Presentismo
        { width: 35 },  // Apellido y Nombres
        { width: 12 },  // Legajo
        { width: 14 },  // DNI N°
        { width: 25 },  // Localidad
        { width: 20 },  // Teléfono Principal
        { width: 20 },  // Teléfono Alternativo
        { width: 35 }   // Firma
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomina_presentismo_${companiaReporte.replace(/\s+/g, '_').toLowerCase()}_${selectedFecha}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Error al generar Excel');
      setTimeout(() => setError(null), 3500);
    } finally {
      setDescargando(false);
    }
  };

  const handlePrintPreview = async () => {
    setDescargando(true);
    setError(null);
    try {
      if (!fechaValida) {
        throw new Error('Seleccione una fecha válida para el reporte.');
      }
      const { cadetes, registros, licenciaPorCadete } = await cargarDatosReporte(selectedFecha);

      const filtrados = cadetes.filter((c) => {
        const matchesActivo = c.activo !== false;
        const matchesCompania = c.compania === companiaReporte;
        // Fuerza efectiva en la fecha del reporte (una baja posterior no lo saca)
        const esConformadorFuerzaEfectiva = esEfectivoEnFecha(c, selectedFecha);
        return matchesActivo && matchesCompania && esConformadorFuerzaEfectiva;
      });

      if (filtrados.length === 0) {
        throw new Error('No se encontraron cadetes activos para la compañía seleccionada.');
      }

      filtrados.sort((a, b) => {
        const apellidoA = (a.apellido || '').trim().toUpperCase();
        const apellidoB = (b.apellido || '').trim().toUpperCase();
        if (apellidoA !== apellidoB) {
          return apellidoA.localeCompare(apellidoB, 'es', { sensitivity: 'base' });
        }
        const nombresA = (a.nombres || '').trim().toUpperCase();
        const nombresB = (b.nombres || '').trim().toUpperCase();
        return nombresA.localeCompare(nombresB, 'es', { sensitivity: 'base' });
      });

      const [year, month, day] = selectedFecha.split('-');
      const fechaFormateada = `${day}/${month}/${year}`;

      // Convertir imagen del sello a Base64 para que html2canvas la renderice offline
      const selloBase64 = await cargarSelloBase64();

      // Paginación: 45 cadetes por hoja A4 (la 2da hoja arranca en el 46), numeración continua
      const PER_PAGE = 45;
      const totalPaginas = Math.ceil(filtrados.length / PER_PAGE);

      const buildRowHtml = (c: any, index: number) => {
        const nro = index + 1;
        const apellidoNombres = `${c.apellido || ''} ${c.nombres || ''}`.trim().toUpperCase();
        const legajoVal = (c.legajo !== null && c.legajo !== undefined && String(c.legajo).trim() !== '') ? String(c.legajo) : 'S/LEGAJO';
        const dniVal = c.dni || '';
        const localidadVal = (c.localidad || '').toUpperCase();
        const telPrincipalVal = c.telefono_principal || '';
        const telAlternativoVal = c.telefono_alternativo || '';

        const matchingReg = registros.find((r) => r.cadete_id === c.id);
        const horarioPresentismo = horarioPresentismoDe(matchingReg, licenciaPorCadete.get(c.id));

        return `
          <tr>
            <td class="center bold">${escapeHtml(nro)}</td>
            <td class="center font-inter">${escapeHtml(horarioPresentismo)}</td>
            <td class="bold font-inter">${escapeHtml(apellidoNombres)}</td>
            <td class="center">${escapeHtml(legajoVal)}</td>
            <td class="center">${escapeHtml(dniVal)}</td>
            <td>${escapeHtml(localidadVal)}</td>
            <td class="center" style="white-space: nowrap;">${escapeHtml(telPrincipalVal)}</td>
            <td class="center" style="white-space: nowrap;">${escapeHtml(telAlternativoVal)}</td>
            <td></td>
          </tr>
        `;
      };

      const theadHtml = `
        <thead>
          <tr>
            <th style="width: 3%;">N°</th>
            <th style="width: 11%;">Horario de Presentismo</th>
            <th style="width: 22%;">Apellido y Nombres</th>
            <th style="width: 8%;">Legajo</th>
            <th style="width: 10%;">DNI N°</th>
            <th style="width: 11%;">Localidad</th>
            <th style="width: 11%;">Teléfono Principal</th>
            <th style="width: 11%;">Teléfono Alternativo</th>
            <th style="width: 13%;">Firma del Cursante</th>
          </tr>
        </thead>
      `;

      const footerHtml = footerSelloFirmaHtml(selloBase64);

      // Una hoja por bloque de 50; el sello/firma va solo en la última hoja
      const paginas: string[] = [];
      for (let p = 0; p < totalPaginas; p++) {
        const start = p * PER_PAGE;
        const pageCadetes = filtrados.slice(start, start + PER_PAGE);
        const rows = pageCadetes.map((c, i) => buildRowHtml(c, start + i)).join('');
        const isLast = p === totalPaginas - 1;
        paginas.push(`
          <div class="report-page">
            <div class="header">
              <h1 style="font-size: 11px; font-weight: 800; margin: 0; letter-spacing: 0.5px;">NÓMINA DE PRESENTISMO - COMPAÑÍA: ${escapeHtml(companiaReporte.toUpperCase())}</h1>
              <div style="font-size: 9px; font-weight: 700; text-align: right;">
                FECHA: ${escapeHtml(fechaFormateada)}<br>
                <span style="font-size: 8px; font-weight: 600;">Hoja ${p + 1} de ${totalPaginas}</span>
              </div>
            </div>
            <table>
              ${theadHtml}
              <tbody>
                ${rows}
              </tbody>
            </table>
            ${isLast ? footerHtml : ''}
          </div>
        `);
      }

      // iframe A4 aislado + html2canvas (scale 3) + jsPDF, compartido con el
      // reporte de novedades (lib/reportePdf.ts). Mismo resultado que antes.
      try {
        await htmlAPdf(paginas, `nomina_presentismo_${companiaReporte.replace(/\s+/g, '_').toLowerCase()}_${selectedFecha}.pdf`);
      } catch (pdfErr: any) {
        console.error('Error durante la generación de PDF:', pdfErr);
        throw new Error('Error al compilar el PDF.');
      }
      setDescargando(false);
    } catch (err: any) {
      setError(err.message || 'Error al generar PDF');
      setTimeout(() => setError(null), 3500);
      setDescargando(false);
    }
  };

  // ── Novedades y Licencias por período (JSON / xlsx del backend nuevo) ──
  const mensajeErrorNovedades = (err: unknown): string => {
    if (requiereBackendNuevo(err)) return MSG_REQUIERE_BACKEND_NUEVO;
    if (esErrorDeRed(err) || !navigator.onLine) return 'Sin conexión: este reporte se genera en el servidor.';
    const msg = (err as { message?: string } | null)?.message;
    return msg && msg !== 'HTTP_ERROR' ? msg : 'Error al generar el reporte de novedades.';
  };

  const validarSeleccionNovedades = (): number => {
    if (novErrorRango) throw new Error(novErrorRango);
    const companiaId = companiaIdDe(novCompaniaReporte);
    if (!companiaId) throw new Error('Seleccione una compañía válida.');
    if (!navigator.onLine) throw new Error('Sin conexión: este reporte se genera en el servidor.');
    return companiaId;
  };

  const cargarNovedades = async (): Promise<ReporteNovedades> => {
    const companiaId = validarSeleccionNovedades();
    const res = await api.getReporteNovedades(companiaId, novDesde, novHasta);
    if (!res.success || !res.data) throw new Error(res.error || 'No se pudo obtener el reporte.');
    setNovReporte(res.data);
    return res.data;
  };

  const nombreArchivoNovedades = (ext: string) =>
    `novedades_licencias_${novCompaniaReporte.replace(/\s+/g, '_').toLowerCase()}_${novDesde}_${novHasta}.${ext}`;

  const handleNovedadesResumen = async () => {
    setNovCargando('resumen');
    setNovError(null);
    try {
      await cargarNovedades();
    } catch (err) {
      setNovError(mensajeErrorNovedades(err));
    } finally {
      setNovCargando(null);
    }
  };

  const handleNovedadesExcel = async () => {
    setNovCargando('excel');
    setNovError(null);
    setDescargando(true);
    try {
      const companiaId = validarSeleccionNovedades();
      const blob = await api.descargarReporteNovedadesXlsx(companiaId, novDesde, novHasta);
      descargarBlob(blob, nombreArchivoNovedades('xlsx'));
    } catch (err) {
      setNovError(mensajeErrorNovedades(err));
    } finally {
      setNovCargando(null);
      setDescargando(false);
    }
  };

  const handleNovedadesPdf = async () => {
    setNovCargando('pdf');
    setNovError(null);
    setDescargando(true);
    try {
      const reporte = await cargarNovedades();
      const selloBase64 = await cargarSelloBase64();
      const paginas = construirPaginasNovedades(reporte, selloBase64);
      try {
        await htmlAPdf(paginas, nombreArchivoNovedades('pdf'));
      } catch (pdfErr) {
        console.error('Error durante la generación del PDF de novedades:', pdfErr);
        throw new Error('Error al compilar el PDF.');
      }
    } catch (err) {
      setNovError(mensajeErrorNovedades(err));
    } finally {
      setNovCargando(null);
      setDescargando(false);
    }
  };

  // ── Parte de Novedades (JSON / xlsx del backend nuevo) ──
  const validarSeleccionParte = (): number => {
    if (parteErrorFecha) throw new Error(parteErrorFecha);
    if (!parteCompaniaId) throw new Error('Seleccione una compañía válida.');
    if (!navigator.onLine) throw new Error(MSG_PARTE_SIN_CONEXION);
    return parteCompaniaId;
  };

  // Guarda el encabezado al perder el foco si cambió respecto a lo que tiene el servidor
  const guardarParametrosParte = async () => {
    if (!parteCompaniaId || !parteParamsServidor) return;
    const datos: ParametrosCompania = { jefe_seccion: parteJefe.trim(), instructores: parteInstructores.trim() };
    if (datos.jefe_seccion === parteParamsServidor.jefe_seccion && datos.instructores === parteParamsServidor.instructores) return;
    const req = ++parteParamsRequest.current;
    setParteParamsEstado('guardando');
    setParteParamsError(null);
    try {
      if (!navigator.onLine) throw new Error('Sin conexión: el encabezado del parte se guarda en el servidor.');
      const res = await api.putParametrosCompania(parteCompaniaId, datos);
      if (req !== parteParamsRequest.current) return;
      if (!res.success) throw new Error(res.error || 'No se pudo guardar el encabezado del parte.');
      const guardados = res.data
        ? { jefe_seccion: res.data.jefe_seccion ?? datos.jefe_seccion, instructores: res.data.instructores ?? datos.instructores }
        : datos;
      setParteParamsServidor(guardados);
      setParteParamsEstado('guardado');
      setTimeout(() => {
        if (req === parteParamsRequest.current) setParteParamsEstado(null);
      }, 2500);
    } catch (err) {
      if (req !== parteParamsRequest.current) return;
      setParteParamsEstado(null);
      setParteParamsError(mensajeErrorParte(err, 'No se pudo guardar el encabezado del parte.'));
    }
  };

  const handleParteVistaPrevia = async () => {
    setParteCargando('vista');
    setParteError(null);
    try {
      const companiaId = validarSeleccionParte();
      const res = await api.getParteNovedades(companiaId, parteFecha);
      if (!res.success || !res.data) throw new Error(res.error || 'No se pudo obtener el parte.');
      setParte(res.data);
    } catch (err) {
      setParteError(mensajeErrorParte(err, 'Error al generar el parte de novedades.'));
    } finally {
      setParteCargando(null);
    }
  };

  const handleParteExcel = async () => {
    setParteCargando('excel');
    setParteError(null);
    setDescargando(true);
    try {
      const companiaId = validarSeleccionParte();
      const blob = await api.descargarParteNovedadesXlsx(companiaId, parteFecha);
      descargarBlob(blob, `parte_${parteCompaniaReporte.replace(/\s+/g, '_').toLowerCase()}_${parteFecha}.xlsx`);
    } catch (err) {
      setParteError(mensajeErrorParte(err, 'Error al generar el parte de novedades.'));
    } finally {
      setParteCargando(null);
      setDescargando(false);
    }
  };

  // Catálogo del panel admin: activos (RMH, ART, LE, PE primero) y luego inactivos
  const tiposOrdenadosAdmin = [
    ...tiposLicenciaActivosOrdenados(tiposLicencias),
    ...tiposLicencias.filter((t) => t.activo === false),
  ];

  return (
    <div className="p-4 animate-fade-in">
      <Toast visible={descargando} error={error} />

      {/* Header section */}
      <section className="mb-6">
        <span
          className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: '#735c00' }}
        >
          Institucional
        </span>
        <h2
          className="text-2xl font-bold mt-0.5"
          style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
        >
          Centro de Reportes
        </h2>
        <p className="text-sm mt-1" style={{ color: '#43474f' }}>
          Formularios oficiales para la gestión del personal y la operatividad del sistema SGCC.
        </p>
      </section>

      {/* Tarjeta de Generación del Reporte "Nómina de Presentismo" */}
      <section
        className="rounded-2xl border p-6 bg-white shadow-sm flex flex-col gap-6 animate-fade-in"
        style={{ borderColor: '#c3c6d1' }}
      >
        <div className="flex items-start gap-4 pb-4 border-b border-[#c3c6d1]/40">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#001e40' }}
          >
            <FileText size={24} strokeWidth={2} style={{ color: '#fed65b' }} />
          </div>
          <div>
            <h3
              className="text-lg font-bold"
              style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
            >
              NÓMINA DE PRESENTISMO
            </h3>
            <p className="text-xs text-[#43474f] mt-1 leading-relaxed">
              Planilla institucional para el control manual y recolección de firmas en lapicera de los cursantes.
            </p>
          </div>
        </div>

        {/* Inputs en cuadrícula */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Selector de Compañía */}
          <div className="space-y-1.5">
            <label
              className="text-[10px] font-bold uppercase tracking-wider block"
              style={{ color: '#737780' }}
              htmlFor="reporte-compania"
            >
              Compañía
            </label>
            <div className="relative">
              <select
                id="reporte-compania"
                className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium appearance-none cursor-pointer disabled:cursor-not-allowed disabled:bg-[#f6f3f2]"
                style={{ color: '#001e40' }}
                value={companiaReporte}
                onChange={(e) => { if (!companiaForzada) setSelectedCompania(e.target.value); }}
                disabled={!!companiaForzada}
                title={companiaForzada ? 'Compañía asignada a su usuario' : undefined}
              >
                {COMPANIAS.map((cia) => (
                  <option key={cia} value={cia}>
                    {cia}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#737780]">
                <Users size={16} />
              </div>
            </div>
          </div>

          {/* Selector de Fecha */}
          <div className="space-y-1.5">
            <label
              className="text-[10px] font-bold uppercase tracking-wider block"
              style={{ color: '#737780' }}
              htmlFor="reporte-fecha"
            >
              Fecha del Reporte
            </label>
            <div className="relative">
              <input
                id="reporte-fecha"
                type="date"
                className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium cursor-pointer"
                style={{ color: '#001e40' }}
                value={selectedFecha}
                onChange={(e) => setSelectedFecha(e.target.value)}
              />
            </div>
            {!fechaValida && (
              <p className="text-[11px] font-medium" style={{ color: '#ba1a1a' }}>
                Seleccione una fecha para generar el reporte.
              </p>
            )}
          </div>
        </div>

        {/* Acciones del Reporte */}
        <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
          {/* Descargar Excel */}
          <button
            onClick={handleExportExcel}
            disabled={descargando || !fechaValida}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95 disabled:opacity-50 hover:bg-[#002a5c]"
            style={{ backgroundColor: '#001e40', fontFamily: 'Inter, sans-serif' }}
          >
            <FileSpreadsheet size={16} strokeWidth={2} style={{ color: '#fed65b' }} />
            Descargar Excel
          </button>

          {/* Descargar PDF */}
          <button
            onClick={handlePrintPreview}
            disabled={descargando || !fechaValida}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-2 transition-all active:scale-95 disabled:opacity-50 hover:bg-[#eae8e7]/30"
            style={{
              borderColor: '#001e40',
              color: '#001e40',
              fontFamily: 'Inter, sans-serif',
              backgroundColor: 'transparent',
            }}
          >
            <FileText size={16} strokeWidth={2} />
            Descargar PDF
          </button>
        </div>
      </section>

      {/* Novedades y Licencias por período (requiere backend nuevo) */}
      <section
        className="mt-6 rounded-2xl border p-6 bg-white shadow-sm flex flex-col gap-6 animate-fade-in"
        style={{ borderColor: '#c3c6d1' }}
      >
        <div className="flex items-start gap-4 pb-4 border-b border-[#c3c6d1]/40">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#001e40' }}
          >
            <ClipboardList size={24} strokeWidth={2} style={{ color: '#fed65b' }} />
          </div>
          <div>
            <h3
              className="text-lg font-bold"
              style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
            >
              NOVEDADES Y LICENCIAS POR PERÍODO
            </h3>
            <p className="text-xs text-[#43474f] mt-1 leading-relaxed">
              Ausencias (ACA, ASA, RMH, ART, LE, PE), tardanzas y licencias de la compañía en un rango de fechas. Se genera en el servidor (requiere conexión).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {/* Compañía */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="nov-compania">
              Compañía
            </label>
            <div className="relative">
              <select
                id="nov-compania"
                className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium appearance-none cursor-pointer disabled:cursor-not-allowed disabled:bg-[#f6f3f2]"
                style={{ color: '#001e40' }}
                value={novCompaniaReporte}
                onChange={(e) => { if (!companiaForzada) setNovCompania(e.target.value); }}
                disabled={!!companiaForzada}
                title={companiaForzada ? 'Compañía asignada a su usuario' : undefined}
              >
                {COMPANIAS.map((cia) => (
                  <option key={cia} value={cia}>{cia}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#737780]">
                <Users size={16} />
              </div>
            </div>
          </div>

          {/* Desde */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="nov-desde">
              Desde
            </label>
            <input
              id="nov-desde"
              type="date"
              className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium cursor-pointer"
              style={{ color: '#001e40' }}
              value={novDesde}
              onChange={(e) => setNovDesde(e.target.value)}
            />
          </div>

          {/* Hasta */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="nov-hasta">
              Hasta
            </label>
            <input
              id="nov-hasta"
              type="date"
              className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium cursor-pointer"
              style={{ color: '#001e40' }}
              value={novHasta}
              onChange={(e) => setNovHasta(e.target.value)}
            />
            {novErrorRango && (
              <p className="text-[11px] font-medium" style={{ color: '#ba1a1a' }}>{novErrorRango}</p>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
          <button
            onClick={handleNovedadesResumen}
            disabled={!!novCargando || descargando || !!novErrorRango}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-2 transition-all active:scale-95 disabled:opacity-50 hover:bg-[#eae8e7]/30"
            style={{ borderColor: '#001e40', color: '#001e40', fontFamily: 'Inter, sans-serif', backgroundColor: 'transparent' }}
          >
            {novCargando === 'resumen'
              ? <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" />
              : <CalendarRange size={16} strokeWidth={2} />}
            Ver resumen
          </button>
          <button
            onClick={handleNovedadesExcel}
            disabled={!!novCargando || descargando || !!novErrorRango}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95 disabled:opacity-50 hover:bg-[#002a5c]"
            style={{ backgroundColor: '#001e40', fontFamily: 'Inter, sans-serif' }}
          >
            {novCargando === 'excel'
              ? <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" style={{ color: '#fed65b' }} />
              : <FileSpreadsheet size={16} strokeWidth={2} style={{ color: '#fed65b' }} />}
            Excel
          </button>
          <button
            onClick={handleNovedadesPdf}
            disabled={!!novCargando || descargando || !!novErrorRango}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-2 transition-all active:scale-95 disabled:opacity-50 hover:bg-[#eae8e7]/30"
            style={{ borderColor: '#001e40', color: '#001e40', fontFamily: 'Inter, sans-serif', backgroundColor: 'transparent' }}
          >
            {novCargando === 'pdf'
              ? <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" />
              : <FileText size={16} strokeWidth={2} />}
            PDF
          </button>
        </div>

        {novError && (
          <div
            className="flex items-start gap-2 p-3 rounded-xl border text-xs leading-snug animate-fade-in"
            style={{ backgroundColor: '#ffdad6', borderColor: 'rgba(186,26,26,0.25)', color: '#93000a' }}
          >
            <AlertCircle size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span style={{ fontFamily: 'Inter, sans-serif' }}>{novError}</span>
          </div>
        )}

        {/* Resumen en pantalla */}
        {novReporteVigente && (
          <div className="rounded-xl border border-[#c3c6d1] bg-[#fbf9f8] p-4 space-y-3 animate-fade-in">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Resumen · {novReporteVigente.compania.nombre}
              </h4>
              <span className="text-[10px] font-semibold text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
                {fechaCorta(novReporteVigente.desde)} al {fechaCorta(novReporteVigente.hasta)} · {novReporteVigente.resumen.dias} días
              </span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {CODIGOS_NOVEDAD.map((cod) => {
                const sc = estiloCodigo(cod);
                const n = novReporteVigente.resumen.por_codigo?.[cod] ?? 0;
                return (
                  <div key={cod} className="rounded-lg border py-2 text-center" style={{ backgroundColor: sc.bg, borderColor: sc.border, opacity: n === 0 ? 0.55 : 1 }} title={NOMBRE_CODIGO[cod]}>
                    <p className="text-lg font-black tabular-nums leading-none" style={{ color: sc.color, fontFamily: 'Inter, sans-serif' }}>{n}</p>
                    <p className="text-[9px] font-extrabold uppercase tracking-wider mt-1" style={{ color: sc.color, fontFamily: 'Inter, sans-serif' }}>{cod}</p>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: 'Tardanzas', value: novReporteVigente.resumen.tardes },
                { label: 'Novedades', value: novReporteVigente.resumen.total_novedades },
                { label: 'Licencias', value: novReporteVigente.resumen.licencias },
              ].map((m) => (
                <div key={m.label} className="rounded-lg border border-[#c3c6d1] bg-white py-2">
                  <p className="text-base font-black tabular-nums leading-none text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{m.value}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider mt-1 text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>{m.label}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {novReporteVigente.cadetes.length} cadetes en la compañía · generado {new Date(novReporteVigente.generado_en).toLocaleString('es-AR')}
            </p>
          </div>
        )}
      </section>

      {/* Parte de Novedades (parte diario oficial; requiere backend nuevo) */}
      <section
        className="mt-6 rounded-2xl border p-6 bg-white shadow-sm flex flex-col gap-6 animate-fade-in"
        style={{ borderColor: '#c3c6d1' }}
      >
        <div className="flex items-start gap-4 pb-4 border-b border-[#c3c6d1]/40">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#001e40' }}
          >
            <ScrollText size={24} strokeWidth={2} style={{ color: '#fed65b' }} />
          </div>
          <div>
            <h3
              className="text-lg font-bold"
              style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
            >
              PARTE DE NOVEDADES
            </h3>
            <p className="text-xs text-[#43474f] mt-1 leading-relaxed">
              Parte diario oficial de la compañía (Excel). Se genera en el servidor (requiere conexión).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Compañía */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="parte-compania">
              Compañía
            </label>
            <div className="relative">
              <select
                id="parte-compania"
                className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium appearance-none cursor-pointer disabled:cursor-not-allowed disabled:bg-[#f6f3f2]"
                style={{ color: '#001e40' }}
                value={parteCompaniaReporte}
                onChange={(e) => { if (!companiaForzada) setParteCompania(e.target.value); }}
                disabled={!!companiaForzada}
                title={companiaForzada ? 'Compañía asignada a su usuario' : undefined}
              >
                {COMPANIAS.map((cia) => (
                  <option key={cia} value={cia}>{cia}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#737780]">
                <Users size={16} />
              </div>
            </div>
          </div>

          {/* Fecha del parte */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="parte-fecha">
              Fecha del Parte
            </label>
            <input
              id="parte-fecha"
              type="date"
              max={hoyArgentina}
              className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium cursor-pointer"
              style={{ color: '#001e40' }}
              value={parteFecha}
              onChange={(e) => setParteFecha(e.target.value)}
            />
            {parteErrorFecha && (
              <p className="text-[11px] font-medium" style={{ color: '#ba1a1a' }}>{parteErrorFecha}</p>
            )}
          </div>

          {/* Encabezado: Jefe de Sección */}
          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="parte-jefe">
              Jefe de Sección
            </label>
            <input
              id="parte-jefe"
              type="text"
              className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium placeholder:text-[#a8abb4] placeholder:font-normal disabled:bg-[#f6f3f2]"
              style={{ color: '#001e40' }}
              placeholder={PLACEHOLDER_JEFE_SECCION}
              value={parteJefe}
              onChange={(e) => setParteJefe(e.target.value)}
              onBlur={guardarParametrosParte}
              disabled={!parteParamsServidor}
              autoComplete="off"
            />
          </div>

          {/* Encabezado: Instructores */}
          <div className="space-y-1.5 sm:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: '#737780' }} htmlFor="parte-instructores">
                Instructores
              </label>
              {parteParamsEstado === 'cargando' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <RefreshCw size={12} strokeWidth={2} className="animate-spin-slow" /> Cargando encabezado…
                </span>
              )}
              {parteParamsEstado === 'guardando' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <RefreshCw size={12} strokeWidth={2} className="animate-spin-slow" /> Guardando…
                </span>
              )}
              {parteParamsEstado === 'guardado' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold animate-fade-in" style={{ color: '#1b6b3a', fontFamily: 'Inter, sans-serif' }}>
                  <Check size={12} strokeWidth={3} /> Guardado
                </span>
              )}
            </div>
            <input
              id="parte-instructores"
              type="text"
              className="w-full p-3 border border-[#c3c6d1] rounded-xl text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium placeholder:text-[#a8abb4] placeholder:font-normal disabled:bg-[#f6f3f2]"
              style={{ color: '#001e40' }}
              placeholder={PLACEHOLDER_INSTRUCTORES}
              value={parteInstructores}
              onChange={(e) => setParteInstructores(e.target.value)}
              onBlur={guardarParametrosParte}
              disabled={!parteParamsServidor}
              autoComplete="off"
            />
            <p className="text-[10px] text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
              El encabezado se guarda por compañía al salir del campo y se imprime en el parte.
            </p>
            {parteParamsError && (
              <p className="text-[11px] font-medium" style={{ color: '#ba1a1a' }}>{parteParamsError}</p>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
          <button
            onClick={handleParteVistaPrevia}
            disabled={!!parteCargando || descargando || !!parteErrorFecha}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-2 transition-all active:scale-95 disabled:opacity-50 hover:bg-[#eae8e7]/30"
            style={{ borderColor: '#001e40', color: '#001e40', fontFamily: 'Inter, sans-serif', backgroundColor: 'transparent' }}
          >
            {parteCargando === 'vista'
              ? <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" />
              : <Eye size={16} strokeWidth={2} />}
            Vista previa
          </button>
          <button
            onClick={handleParteExcel}
            disabled={!!parteCargando || descargando || !!parteErrorFecha}
            className="w-full sm:w-auto h-12 px-6 rounded-xl text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95 disabled:opacity-50 hover:bg-[#002a5c]"
            style={{ backgroundColor: '#001e40', fontFamily: 'Inter, sans-serif' }}
          >
            {parteCargando === 'excel'
              ? <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" style={{ color: '#fed65b' }} />
              : <FileSpreadsheet size={16} strokeWidth={2} style={{ color: '#fed65b' }} />}
            Excel
          </button>
        </div>

        {parteError && (
          <div
            className="flex items-start gap-2 p-3 rounded-xl border text-xs leading-snug animate-fade-in"
            style={{ backgroundColor: '#ffdad6', borderColor: 'rgba(186,26,26,0.25)', color: '#93000a' }}
          >
            <AlertCircle size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
            <span style={{ fontFamily: 'Inter, sans-serif' }}>{parteError}</span>
          </div>
        )}

        {/* Vista previa en pantalla */}
        {parteVigente && (
          <div className="rounded-xl border border-[#c3c6d1] bg-[#fbf9f8] p-4 space-y-4 animate-fade-in">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Parte · {parteVigente.compania.nombre_parte || parteVigente.compania.nombre}
              </h4>
              <span className="text-[10px] font-semibold text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>
                {parteVigente.fecha_texto || fechaCorta(parteVigente.fecha)}
              </span>
            </div>

            {/* Totales */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'Fuerza convocada', value: parteVigente.totales.convocada, destacado: false },
                { label: 'A/A/C', value: parteVigente.totales.aac, destacado: false },
                { label: 'Exclusiones', value: parteVigente.totales.exclusiones, destacado: false },
                { label: 'Desistimientos', value: parteVigente.totales.desistimientos, destacado: false },
                { label: 'Fuerza efectiva', value: parteVigente.totales.efectiva, destacado: true },
                { label: 'Presentes', value: parteVigente.totales.presentes, destacado: false },
                { label: 'Ausentes', value: parteVigente.totales.ausentes, destacado: false },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded-lg border py-2.5 px-2 text-center"
                  style={m.destacado
                    ? { backgroundColor: '#001e40', borderColor: '#001e40' }
                    : { backgroundColor: '#ffffff', borderColor: '#c3c6d1' }}
                >
                  <p
                    className="text-lg font-black tabular-nums leading-none"
                    style={{ color: m.destacado ? '#fed65b' : '#001e40', fontFamily: 'Inter, sans-serif' }}
                  >
                    {m.value}
                  </p>
                  <p
                    className="text-[9px] font-bold uppercase tracking-wider mt-1"
                    style={{ color: m.destacado ? '#f3f0f0' : '#737780', fontFamily: 'Inter, sans-serif' }}
                  >
                    {m.label}
                  </p>
                </div>
              ))}
            </div>

            {parteSinMarcar > 0 && (
              <div
                className="flex items-start gap-2 p-3 rounded-xl border text-xs leading-snug"
                style={{ backgroundColor: '#fff3cd', borderColor: 'rgba(115,92,0,0.3)', color: '#735c00' }}
              >
                <AlertCircle size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
                <span style={{ fontFamily: 'Inter, sans-serif' }}>
                  Hay {parteSinMarcar} {parteSinMarcar === 1 ? 'cadete' : 'cadetes'} sin marcar en esta fecha; el parte {parteSinMarcar === 1 ? 'lo' : 'los'} omite.
                </span>
              </div>
            )}

            {/* Secciones */}
            <div className="space-y-3">
              {parteVigente.secciones.map((sec) => {
                const sc = estiloCodigo(sec.clave);
                return (
                  <div key={sec.clave} className="rounded-lg border border-[#c3c6d1] bg-white overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#c3c6d1]/40 bg-[#f6f3f2]">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <span
                          className="px-2 py-0.5 rounded border font-black uppercase tracking-tight text-[10px] shrink-0"
                          style={{ backgroundColor: sc.bg, color: sc.color, borderColor: sc.border }}
                        >
                          {sec.clave}
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#001e40] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {sec.titulo}
                        </span>
                      </span>
                      <span className="text-xs font-black tabular-nums text-[#001e40] shrink-0" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {sec.cantidad}
                      </span>
                    </div>
                    {sec.filas.length === 0 ? (
                      <p className="px-3 py-2 text-[11px] text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>Sin novedades.</p>
                    ) : (
                      <ul className="divide-y divide-[#c3c6d1]/40">
                        {sec.filas.map((f) => (
                          <li key={`${sec.clave}-${f.cadete_id}-${f.n}`} className="px-3 py-2 text-[11px] leading-snug" style={{ color: '#1b1c1c', fontFamily: 'Inter, sans-serif' }}>
                            <span className="font-black tabular-nums text-[#737780]">{f.n}</span>
                            <span className="text-[#c3c6d1]"> · </span>
                            <span className="font-bold text-[#001e40]">{f.nombre}</span>
                            {f.dni && (<><span className="text-[#c3c6d1]"> · </span><span className="tabular-nums">DNI {f.dni}</span></>)}
                            {f.motivo && (<><span className="text-[#c3c6d1]"> · </span><span className="text-[#43474f]">{f.motivo}</span></>)}
                            {(f.desde || f.hasta) && (
                              <>
                                <span className="text-[#c3c6d1]"> · </span>
                                <span className="text-[#43474f] tabular-nums">
                                  {f.desde ? `desde ${fechaCorta(f.desde)}` : ''}
                                  {f.desde && f.hasta ? ' ' : ''}
                                  {f.hasta ? `hasta ${fechaCorta(f.hasta)}` : ''}
                                </span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Panel de administración para Configuración de Licencias Académicas */}
      {isAdmin && (
        <section
          className="mt-8 rounded-xl border p-5 bg-[#ffffff] border-[#c3c6d1] shadow-sm flex flex-col gap-4 animate-fade-in"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-[#c3c6d1]/40">
            <div>
              <h3
                className="font-bold text-lg"
                style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
              >
                Configuración de Licencias Académicas
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#43474f' }}>
                Defina los tipos de licencias globales autorizados en el sistema (RMH, ART, LE, PE). Los inactivos se muestran en gris y pueden reactivarse.
              </p>
            </div>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-[10px] text-white active:scale-95 transition-all flex items-center gap-1.5 shadow-sm hover:opacity-90"
              style={{ backgroundColor: '#001e40', fontFamily: 'Inter, sans-serif' }}
            >
              <PlusCircle size={16} strokeWidth={2} />
              Nuevo Tipo
            </button>
          </div>

          {loadingLicencias && tiposLicencias.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-[#737780]">
              <RefreshCw size={28} strokeWidth={1.6} className="animate-spin-slow" />
              <span className="text-xs font-semibold">Cargando catálogo...</span>
            </div>
          ) : tiposLicencias.length === 0 ? (
            <div className="py-8 border-2 border-dashed border-[#c3c6d1] rounded-xl text-center bg-[#fbf9f8] px-4">
              <Award size={28} strokeWidth={1.4} className="text-[#c3c6d1] mb-1 mx-auto" />
              <p className="text-xs font-bold uppercase tracking-wider text-[#43474f]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Sin Licencias Configuradas
              </p>
              <p className="text-[10px] text-[#737780] mt-0.5">Use el botón superior para crear las licencias autorizadas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-[#c3c6d1]">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#c3c6d1] bg-[#f6f3f2]" style={{ color: '#43474f' }}>
                    <th className="p-3 font-bold uppercase tracking-wider">Código</th>
                    <th className="p-3 font-bold uppercase tracking-wider">Nombre</th>
                    <th className="p-3 font-bold uppercase tracking-wider">Descripción</th>
                    <th className="p-3 text-right font-bold uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tiposOrdenadosAdmin.map((t) => {
                    const inactivo = t.activo === false;
                    return (
                    <tr
                      key={t.id}
                      className={`border-b border-[#c3c6d1]/40 hover:bg-[#fbf9f8] transition-colors ${inactivo ? 'bg-[#f6f3f2]' : ''}`}
                      style={{ color: inactivo ? '#737780' : '#1b1c1c' }}
                    >
                      <td className="p-3 font-extrabold text-[#001e40]">
                        <span
                          className={`px-2.5 py-1 rounded border font-black uppercase tracking-tight text-[10px] ${
                            inactivo ? 'bg-[#eae8e7] border-[#c3c6d1] text-[#737780]' : 'bg-[#f3e8ff] border-[#d8b4fe] text-[#6b21a8]'
                          }`}
                        >
                          {t.codigo}
                        </span>
                      </td>
                      <td className={`p-3 font-bold ${inactivo ? 'text-[#737780]' : 'text-[#001e40]'}`}>
                        <span className="inline-flex items-center gap-2">
                          {t.nombre}
                          {inactivo && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider border border-[#c3c6d1] bg-white text-[#737780]">
                              Inactivo
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={`p-3 max-w-xs truncate ${inactivo ? 'text-[#737780]' : 'text-[#43474f]'}`}>{t.descripcion || 'Sin descripción'}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEditModal(t)}
                            className="p-2 hover:bg-[#eae8e7] rounded text-[#43474f] hover:text-[#001e40] transition-colors active:scale-90"
                            title="Editar"
                          >
                            <Pencil size={16} strokeWidth={2} />
                          </button>
                          {inactivo ? (
                            <button
                              onClick={() => handleReactivarTipoLicencia(t)}
                              className="p-2 hover:bg-green-50 rounded text-[#737780] hover:text-green-700 transition-colors active:scale-90"
                              title="Reactivar"
                            >
                              <RotateCcw size={16} strokeWidth={2} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteTipoLicencia(t.id)}
                              className="p-2 hover:bg-red-50 rounded text-[#737780] hover:text-red-600 transition-colors active:scale-90"
                              title="Desactivar"
                            >
                              <Trash2 size={16} strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Bottom Sheet — Creación/Edición de Tipo de Licencia */}
      <BottomSheet
        open={modalLicenciaOpen}
        onClose={closeModal}
        title={editingLicencia ? 'Editar Tipo de Licencia' : 'Nuevo Tipo de Licencia'}
        subtitle="Configuración del Catálogo Oficial"
        icon={<Award size={18} strokeWidth={1.8} />}
        detent="large"
        footer={
          <div className="flex flex-col gap-3">
            <button
              className="w-full h-12 rounded-[14px] text-ios-subhead font-semibold text-white flex items-center justify-center gap-2 tap-active disabled:opacity-50"
              style={{ backgroundColor: '#001e40' }}
              onClick={handleSaveTipoLicencia}
              disabled={formCargando}
            >
              {formCargando ? (
                <>
                  <RefreshCw size={18} strokeWidth={2} className="animate-spin-slow" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={18} strokeWidth={2} />
                  Guardar Tipo
                </>
              )}
            </button>
            <button
              className="w-full h-12 rounded-[14px] text-ios-subhead font-medium tap-active"
              style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
              onClick={closeModal}
            >
              Cancelar
            </button>
          </div>
        }
      >
        <div className="px-4 py-4">
        <p className="text-xs mb-5 border-l-4 border-[#799dd6] pl-3 py-1 bg-[#f6f3f2]" style={{ color: '#43474f', fontFamily: 'Inter, sans-serif' }}>
          Defina el código y nombre del tipo de licencia académica. El código debe ser una abreviación única de 2 a 4 letras mayúsculas (ej: RMH, ART, LE, PE).
        </p>

        <form onSubmit={handleSaveTipoLicencia} className="space-y-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider block" htmlFor="form-codigo">
              Código de Licencia (Abreviatura) *
            </label>
            <input
              id="form-codigo"
              type="text"
              className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-bold uppercase"
              placeholder="Ej: RMH"
              maxLength={4}
              pattern="[A-Za-z]{2,4}"
              value={formCodigo}
              onChange={(e) => setFormCodigo(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider block" htmlFor="form-nombre">
              Nombre Oficial *
            </label>
            <input
              id="form-nombre"
              type="text"
              className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none font-medium"
              placeholder="Ej: Licencia Médica (RMH)"
              value={formNombre}
              onChange={(e) => setFormNombre(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider block" htmlFor="form-descripcion">
              Descripción / Observaciones
            </label>
            <textarea
              id="form-descripcion"
              className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none resize-none"
              placeholder="Detalle los alcances o justificaciones asociadas a este tipo de licencia..."
              rows={3}
              value={formDescripcion}
              onChange={(e) => setFormDescripcion(e.target.value)}
            />
          </div>
        </form>
        </div>{/* /px-4 py-4 */}
      </BottomSheet>
    </div>
  );
}
